/**
 * schemas.ts — single consolidated table of all 21 widget schemas.
 *
 * Previously: 21 individual packages/widgets/src/{name}/schema.ts files.
 * Now: one file; adding a widget is a 4-line entry here instead of:
 *   mkdir {name}/ && create {name}/schema.ts && add import to registry.ts
 *
 * Order: alphabetical by widget name for searchability.
 *
 * MUST NOT import React.
 */
import { z } from 'zod';
import type { WidgetMeta } from './registry';

// Preserves the literal `name` type so consumers can derive
// `WidgetName = typeof WIDGET_SCHEMAS[number]['name']` (a string-literal union),
// rather than collapsing it to plain `string`.
function defineWidget<N extends string, TProps>(
  meta: { name: N; schema: z.ZodType<TProps>; description: string; rolesAllowedToEmit: ReadonlyArray<string> },
): { name: N; schema: z.ZodType<TProps>; description: string; rolesAllowedToEmit: ReadonlyArray<string> } {
  return meta;
}

export const WIDGET_SCHEMAS = [
  defineWidget({
    name: 'awaiting-finalization',
    schema: z.object({
      subscriptionId: z.string(),
    }),
    description: 'Awaiting officer finalization.',
    rolesAllowedToEmit: ['investor'] as const,
  }),

  defineWidget({
    name: 'candidate-card',
    schema: z.object({
      candidateId: z.string().min(1),
      handle: z.string().min(1),
      channel: z.string().min(1),
      name: z.string().optional(),
      reason_line: z.string().min(1),
      preview_score: z.coerce.number().int().min(0).max(100),
    }),
    description:
      'Lobby candidate awaiting founder decision (Create prospect / Reject).',
    rolesAllowedToEmit: ['outreach-agent'] as const,
  }),

  defineWidget({
    name: 'conversations-list',
    schema: z.object({
      threads: z.array(z.object({
        prospectId: z.string(),
        handle: z.string().optional(),
        lastMessageAt: z.string().optional(),
        snippet: z.string().optional(),
      })),
    }),
    description: 'DM threads list — all recent, or scoped to one prospect.',
    rolesAllowedToEmit: ['outreach-agent'] as const,
  }),

  defineWidget({
    name: 'chart',
    schema: z.object({
      type: z.string().optional(),
      source: z.string().optional(),
      range: z.string().optional(),
    }).passthrough(),
    description: 'Data chart (web-only; not agent-emitted).',
    rolesAllowedToEmit: [] as const,
  }),

  defineWidget({
    name: 'create-subscription',
    schema: z.object({
      contractAddress: z.string().optional(),
      restaurantName: z.string().optional(),
      min: z.string().optional(),
      max: z.string().optional(),
    }).passthrough(),
    description: 'Create subscription flow (web-only; not agent-emitted).',
    rolesAllowedToEmit: [] as const,
  }),

  defineWidget({
    name: 'custody-safe',
    schema: z.object({
      subscriptionId: z.string(),
    }),
    description: 'Safe multi-sig custody confirmation.',
    rolesAllowedToEmit: ['investor'] as const,
  }),

  defineWidget({
    name: 'dm-composer-modal',
    schema: z.object({
      prospectId: z.string(),
      channel: z.enum(['x_dm', 'email', 'linkedin']),
    }),
    description: 'DM composer modal — Phase 4 outreach.',
    rolesAllowedToEmit: ['admin'] as const,
  }),

  defineWidget({
    name: 'draft-card',
    schema: z.object({
      prospectId: z.string().min(1),
      draftId: z.string().min(1),
      channel: z.string().min(1),
      subject: z.string().optional(),
      body_preview: z.string().min(1),
      rationale: z.string().optional(),
    }),
    description:
      'Agent-saved draft awaiting founder review; "Use this draft" populates the prospect Composer.',
    rolesAllowedToEmit: ['outreach-agent'] as const,
  }),

  defineWidget({
    name: 'feed-signals',
    schema: z.object({
      signals: z.array(z.object({
        prospectId: z.string(),
        handle: z.string().optional(),
        kind: z.string(),
        summary: z.string(),
        at: z.string(),
      })),
    }),
    description: 'Recent engagement signals (prospect.engaged / prospect.contacted / prospect.scored) across all prospects.',
    rolesAllowedToEmit: ['outreach-agent'] as const,
  }),

  defineWidget({
    name: 'form-expense',
    schema: z.object({
      restaurantId: z.string().optional(),
    }).passthrough(),
    description: 'Expense entry form (web-only; not agent-emitted).',
    rolesAllowedToEmit: [] as const,
  }),

  defineWidget({
    name: 'founder-decision',
    schema: z.object({
      tool_use_id: z.string().min(1),
      question: z.string().min(1),
      options: z
        .array(
          z.object({ label: z.string().min(1), value: z.string().min(1) }),
        )
        .min(2)
        .max(6),
      sessionId: z.string().min(1),
    }),
    description:
      'Founder must click one option to advance a blocked action (qualify, send, etc); emits outreach.founder.decided and resumes the agent.',
    rolesAllowedToEmit: ['outreach-agent'] as const,
  }),

  defineWidget({
    name: 'form-invest',
    schema: z.object({
      contractId: z.string().optional(),
      walletAddress: z.string().optional(),
      saleEndAt: z.string().optional(),
    }).passthrough(),
    description: 'Investment form (web-only; not agent-emitted).',
    rolesAllowedToEmit: [] as const,
  }),

  defineWidget({
    name: 'form-sale',
    schema: z.object({
      restaurantId: z.string().optional(),
    }).passthrough(),
    description: 'Sale entry form (web-only; not agent-emitted).',
    rolesAllowedToEmit: [] as const,
  }),

  defineWidget({
    name: 'fund-wallet',
    schema: z.object({
      subscriptionId: z.string(),
    }),
    description: 'Fund the investor wallet.',
    rolesAllowedToEmit: ['investor'] as const,
  }),

  defineWidget({
    name: 'inbox-decisions',
    schema: z.object({
      decisions: z.array(z.object({
        prospectId: z.string(),
        question: z.string(),
        requestedAt: z.string(),
      })),
    }),
    description: 'Items requiring founder decision (unresolved outreach.decision.requested events).',
    rolesAllowedToEmit: ['outreach-agent'] as const,
  }),

  defineWidget({
    name: 'kyc-form',
    schema: z.object({
      subscriptionId: z.string(),
    }),
    description: 'Stripe Identity KYC verification flow.',
    rolesAllowedToEmit: ['investor'] as const,
  }),

  defineWidget({
    name: 'kyc-decision',
    schema: z.object({
      tool_use_id: z.string().min(1),
      sessionId: z.string().min(1),
      subscriptionId: z.string().min(1),
      investorId: z.string().min(1),
      rmintId: z.string().min(1),
      profileSnapshot: z.string().optional(),
      kycDocFilenames: z.string().optional(),
      subAgentFindingMarkdown: z.string().optional(),
    }),
    description:
      'Admin KYC review — approve/reject an investor KYC submission. Confirmation widget; round-trips through /decision. kyc-reviewer sub-agent finding optional in v1.',
    rolesAllowedToEmit: ['admin'] as const,
  }),

  defineWidget({
    name: 'kyc-upload',
    schema: z.object({
      subscriptionId: z.string(),
    }),
    description: 'KYC document upload.',
    rolesAllowedToEmit: ['investor'] as const,
  }),

  defineWidget({
    name: 'officer-queue',
    schema: z.object({
      // base64-encoded JSON of the projector's GET /admin/officer/{id}/queue
      // response. Pure display widget — the admin agent emits it from QueueInspector.
      payload: z.string().min(1),
    }),
    description: "Compliance officer review queue (pending / in-progress / completed today). Display-only.",
    rolesAllowedToEmit: ['admin'] as const,
  }),

  defineWidget({
    name: 'outreach-inbox',
    schema: z.object({}),
    description: 'Inbox of newly-discovered prospects awaiting triage.',
    rolesAllowedToEmit: ['admin'] as const,
  }),

  defineWidget({
    name: 'outreach-pipeline',
    schema: z.object({}),
    description: 'Kanban of prospects by stage.',
    rolesAllowedToEmit: ['admin'] as const,
  }),

  defineWidget({
    name: 'outreach-signal-feed',
    schema: z.object({
      days: z.coerce.number().int().min(1).max(90).default(7),
    }),
    description: 'Chronological event feed across outreach.',
    rolesAllowedToEmit: ['admin'] as const,
  }),

  defineWidget({
    name: 'outreach-today-cockpit',
    schema: z.object({
      limit: z.coerce.number().int().min(1).max(50).default(10),
    }),
    description: 'Top-priority prospects with pre-drafted messages.',
    rolesAllowedToEmit: ['admin'] as const,
  }),

  defineWidget({
    name: 'payment',
    schema: z.object({
      subscriptionId: z.string(),
      amountUsd: z.number().int().positive(),
    }),
    description: 'USDC payment confirmation.',
    rolesAllowedToEmit: ['investor'] as const,
  }),

  defineWidget({
    name: 'pipeline-view',
    schema: z.object({
      phases: z.record(z.string(), z.array(z.object({
        prospectId: z.string(),
        handle: z.string().optional(),
      }))),
    }),
    description: 'Funnel kanban — prospects grouped by current phase.',
    rolesAllowedToEmit: ['outreach-agent'] as const,
  }),

  defineWidget({
    name: 'prospect-dm-composer',
    schema: z.object({
      prospectId: z.string(),
    }),
    description: 'Embedded DM composer in prospect detail view.',
    rolesAllowedToEmit: ['admin'] as const,
  }),

  defineWidget({
    name: 'prospect-link',
    schema: z.object({
      prospectId: z.string().min(1),
      label: z.string().optional(),
    }),
    description:
      'Navigation chip to a prospect dossier; clicking routes the cockpit + updates chat scope (α.8 wires the scope side).',
    rolesAllowedToEmit: ['outreach-agent'] as const,
  }),

  defineWidget({
    name: 'prospect-notes',
    schema: z.object({
      prospectId: z.string(),
    }),
    description: 'Free-text notes per prospect.',
    rolesAllowedToEmit: ['admin'] as const,
  }),

  defineWidget({
    name: 'prospect-summary',
    schema: z.object({
      prospectId: z.string(),
    }),
    description: 'Summary card (score, signals, status).',
    rolesAllowedToEmit: ['admin'] as const,
  }),

  defineWidget({
    name: 'prospect-timeline',
    schema: z.object({
      prospectId: z.string(),
      days: z.coerce.number().int().min(1).max(365).default(30),
    }),
    description: 'Per-prospect event timeline.',
    rolesAllowedToEmit: ['admin'] as const,
  }),

  defineWidget({
    name: 'research-finding',
    schema: z.object({
      url: z.string().url(),
      summary: z.string().min(1),
      source: z.string().min(1),
      findingId: z.string().regex(/^find_[a-z0-9_]+$/i),
    }),
    description: 'A single research finding the outreach-agent saved during discovery.',
    rolesAllowedToEmit: ['outreach-agent'] as const,
  }),

  defineWidget({
    name: 'revenue-claim',
    schema: z.object({
      investorAddress: z.string().optional(),
    }).passthrough(),
    description: 'Revenue claim action (web-only; not agent-emitted).',
    rolesAllowedToEmit: [] as const,
  }),

  defineWidget({
    name: 'review',
    schema: z.object({
      subscriptionId: z.string().min(1),
      amountUsd: z.number().int().positive(),
      step: z.enum(['confirm', 'awaiting']).optional(),
    }),
    description: 'Review investment terms before payment.',
    rolesAllowedToEmit: ['investor'] as const,
  }),

  defineWidget({
    name: 'scan-result',
    schema: z.object({
      // base64-encoded JSON of the projector's POST /admin/scan-invariants
      // response. Pure display widget — emitted post-confirm from ScanInvariants.
      payload: z.string().min(1),
    }),
    description: 'Invariant-scan report (scanned count + violations). Display-only.',
    rolesAllowedToEmit: ['admin'] as const,
  }),

  defineWidget({
    name: 'subscribe-allocate-form',
    schema: z.object({
      subscriptionId: z.string(),
      state: z.enum(['pending', 'attested']).optional(),
    }),
    description: 'Subscription allocation form — investor attests investment amount.',
    rolesAllowedToEmit: ['investor'] as const,
  }),

  defineWidget({
    name: 'subscribe-fund-flow',
    schema: z.object({
      subscriptionId: z.string(),
    }),
    description: 'Subscription funding flow — on-ramp or USDC transfer to escrow.',
    rolesAllowedToEmit: ['investor'] as const,
  }),

  defineWidget({
    name: 'subscribe-kyc-action',
    schema: z.object({
      subscriptionId: z.string(),
      state: z.enum(['pending', 'verified']).optional(),
    }),
    description: 'Subscription KYC step — prompt or show verification status.',
    rolesAllowedToEmit: ['investor'] as const,
  }),

  defineWidget({
    name: 'subscribe-mint-confirmation',
    schema: z.object({
      subscriptionId: z.string(),
      state: z.enum(['just', 'holding']).optional(),
    }),
    description: 'Mint confirmation — tokens delivered to investor wallet.',
    rolesAllowedToEmit: ['investor'] as const,
  }),

  defineWidget({
    name: 'subscribe-sign-form',
    schema: z.object({
      subscriptionId: z.string(),
      state: z.enum(['done']).optional(),
    }),
    description: 'Subscription agreement signing step — EIP-712 or PDF e-sign.',
    rolesAllowedToEmit: ['investor'] as const,
  }),

  defineWidget({
    name: 'subscription-sign',
    schema: z.object({
      subscriptionId: z.string(),
    }),
    description: 'EIP-712 typed-data subscription signature.',
    rolesAllowedToEmit: ['investor'] as const,
  }),

  defineWidget({
    name: 'sources-list',
    schema: z.object({
      sources: z.array(z.object({
        sourceId: z.string(),
        kind: z.string().optional(),
        lastRunAt: z.string().optional(),
        prospectsFound: z.number().optional(),
      })),
    }),
    description: 'List of outreach-sources (RSS feeds, X searches, manual seeds) and their run status.',
    rolesAllowedToEmit: ['outreach-agent'] as const,
  }),

  defineWidget({
    name: 'templates-list',
    schema: z.object({
      templates: z.array(z.object({
        name: z.string(),
        kind: z.string().optional(),
        summary: z.string().optional(),
      })),
    }),
    description: 'List message templates / automations available to the agent.',
    rolesAllowedToEmit: ['outreach-agent'] as const,
  }),

  defineWidget({
    name: 'today-queue',
    schema: z.object({
      items: z.array(z.object({
        prospectId: z.string(),
        handle: z.string().optional(),
        reason: z.string(),
        phase: z.string().optional(),
      })),
    }),
    description: 'Compact triage list — prospects needing attention today.',
    rolesAllowedToEmit: ['outreach-agent'] as const,
  }),

  defineWidget({
    name: 'token-delivery',
    schema: z.object({
      subscriptionId: z.string(),
    }),
    description: 'Token delivery confirmation.',
    rolesAllowedToEmit: ['investor'] as const,
  }),

  defineWidget({
    name: 'tx-sign',
    schema: z.object({
      txKind: z.enum(['approve', 'transfer', 'sign']),
      subscriptionId: z.string().optional(),
    }),
    description: 'Generic transaction signing UI.',
    rolesAllowedToEmit: ['investor', 'admin'] as const,
  }),

  defineWidget({
    name: 'workers-list',
    schema: z.object({
      workers: z.array(z.object({
        workerId: z.string(),
        channel: z.string().optional(),
        status: z.string().optional(),
        lastActiveAt: z.string().optional(),
      })),
    }),
    description: 'Outreach worker pool status (per-worker channel + activity).',
    rolesAllowedToEmit: ['outreach-agent'] as const,
  }),

  defineWidget({
    name: 'sequences-list',
    // Field set is the contract — must match SequenceItemSchema in
    // apps/outreach-agent/src/tools/sequences-list.ts EXACTLY. Per
    // feedback_happy_path_tests_need_real_fixtures: loosening required
    // fields to optional hides real shape mismatches at the widget
    // boundary instead of catching them at directive-parse time.
    schema: z.object({
      sequences: z.array(z.object({
        name: z.string(),
        trigger: z.object({ type: z.string(), eventName: z.string() }).passthrough(),
        step_count: z.number().int().nonnegative(),
        enrollment_count: z.number().int().nonnegative(),
        exited_count: z.number().int().nonnegative(),
      })),
    }),
    description: 'Outreach automation sequences — name, trigger, enrollment + step counts per sequence.',
    rolesAllowedToEmit: ['outreach-agent'] as const,
  }),

  defineWidget({
    name: 'sequence-graph',
    schema: z.object({
      automation: z.object({ name: z.string() }).passthrough(),
      enrolled: z.array(z.object({ state: z.string() }).passthrough()),
    }),
    description: 'One automation sequence — step graph + enrolled prospects by state.',
    rolesAllowedToEmit: ['outreach-agent'] as const,
  }),

  defineWidget({
    name: 'device-pairing',
    schema: z.object({
      token: z.string().min(1),
      projectorUrl: z.string().optional(),
      step: z.string().optional(),
    }),
    description: 'Interactive device-pairing wizard (token + QR + multi-step). First tool-level requires-confirmation surface (H.2.4).',
    rolesAllowedToEmit: ['outreach-agent'] as const,
  }),
] as const;

export type WidgetName = typeof WIDGET_SCHEMAS[number]['name'];

export const WIDGET_BY_NAME = Object.fromEntries(
  WIDGET_SCHEMAS.map((w) => [w.name, w]),
) as Record<WidgetName, WidgetMeta>;
